"use server";

import { headers } from "next/headers";
import { submitCentreRegistration } from "@/features/centre-registrations/submit";
import { callDasApi } from "@/lib/das-api";

/**
 * The one place a form hands its data off. It runs on the server (a server
 * action), so the forms can reach the DAS platform API without its key ever
 * reaching the browser.
 *
 * Sign-in, register and forgot password go to the DAS platform, as they did on
 * WordPress. A centre registration is saved here, then sent to the onboarding
 * platform (features/centre-registrations). The contact form has no backend
 * yet, so it comes back with a "not connected" result the form shows like any
 * other failure.
 */

export type FormId =
  "contact" | "centre-registration" | "signin" | "register" | "forgot-password";

export type FormResult = {
  ok: boolean;
  message: string;
  /** Per-field messages, keyed by field name, for forms that validate server-side. */
  errors?: Record<string, string>;
  /** Where to send the browser next (the app, after signing in). */
  redirect?: string;
};

type Data = Record<string, unknown>;

const NOT_CONNECTED: FormResult = {
  ok: false,
  message:
    "Sorry, this form is not available yet. Please email us at info@dasacademies.co.uk.",
};

const SERVER_ERROR: FormResult = {
  ok: false,
  message:
    "Something went wrong on our side. Please try again later or contact support.",
};

export async function submitForm(id: FormId, data: Data): Promise<FormResult> {
  try {
    switch (id) {
      case "signin":
        return await signin(data);
      case "register":
        return await register(data);
      case "forgot-password":
        return await forgotPassword(data);
      case "centre-registration":
        return await centreRegistration(data);
      default:
        return NOT_CONNECTED;
    }
  } catch (error) {
    console.error(`Form "${id}" failed:`, error);

    return SERVER_ERROR;
  }
}

async function centreRegistration(data: Data): Promise<FormResult> {
  const requestHeaders = await headers();
  const ip =
    requestHeaders.get("x-forwarded-for")?.split(",")[0]?.trim() ||
    requestHeaders.get("x-real-ip") ||
    null;
  const result = await submitCentreRegistration(data, ip);
  // The form shows its own confirmation on success, so the message is only for failures.
  return result.ok
    ? { ok: true, message: "Registration received." }
    : { ok: false, message: result.message, errors: result.errors };
}

const text = (data: Data, name: string) =>
  typeof data[name] === "string" ? (data[name] as string).trim() : "";

/**
 * The platform checks the credentials. On WordPress a matching WP user was then
 * created and logged in, but nothing on this site needs a session of its own,
 * so a successful sign-in only hands back the app's redirect.
 */
async function signin(data: Data): Promise<FormResult> {
  const email = text(data, "email");
  const password = typeof data.password === "string" ? data.password : "";

  if (!email || !password) {
    return { ok: false, message: "Please enter your email and password." };
  }

  const response = await callDasApi<{
    success?: boolean;
    redirect_url?: string;
  }>("signin", { email, password });

  if (!response.data?.success) {
    return {
      ok: false,
      message:
        "The email or password you entered is incorrect. Please try again.",
    };
  }

  return {
    ok: true,
    message: "You have successfully signed in. Redirecting you to the app...",
    redirect: response.data.redirect_url || undefined,
  };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The theme's password rule (poppy_validate_field): six or more letters and digits, at least one of each.
const PASSWORD = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;

/**
 * The same checks the WordPress handler made before passing the member on.
 * The platform emails the activation link itself.
 */
async function register(data: Data): Promise<FormResult> {
  const firstName = text(data, "first_name");
  const lastName = text(data, "last_name");
  const email = text(data, "email");
  const password = typeof data.password === "string" ? data.password : "";
  const confirm =
    typeof data.confirm_password === "string" ? data.confirm_password : "";
  const errors: Record<string, string> = {};

  // Club and association accounts are sent off to their own sign-up pages
  // before submitting, so anything else here means a tampered form.
  if (!["member", "club", "hss"].includes(text(data, "account_type"))) {
    return {
      ok: false,
      message:
        "Unexpected error. Please refresh the page and try again or contact support.",
    };
  }

  if (!firstName) {
    errors.first_name = "First name is required.";
  }

  if (!lastName) {
    errors.last_name = "Last name is required.";
  }

  if (!email) {
    errors.email = "Email is required.";
  } else if (!EMAIL.test(email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (!password) {
    errors.password = "Password is required.";
  } else {
    if (!PASSWORD.test(password)) {
      errors.password =
        "Password must be at least 6 characters long and contain at least one letter and one number.";
    }

    if (password !== confirm) {
      errors.confirm_password = "Password confirmation does not match.";
    }
  }

  if (Object.keys(errors).length) {
    return {
      ok: false,
      message: "Please correct the errors below and try again.",
      errors,
    };
  }

  const response = await callDasApi("register_member", {
    first_name: firstName,
    last_name: lastName,
    email,
    password,
    password_confirm: confirm,
    client_platform: "wordpress",
  });

  if (response.status === "fail") {
    console.error("DAS API register_member failed:", response.errors);

    return {
      ok: false,
      message:
        "Something went wrong while registering your account. Please try again or contact support.",
    };
  }

  return {
    ok: true,
    message:
      "You have successfully registered. Please check your email to activate your account.",
  };
}

/**
 * Always answers the same way, so the form cannot be used to find out which
 * emails have an account.
 */
async function forgotPassword(data: Data): Promise<FormResult> {
  const email = text(data, "email");

  if (email) {
    await callDasApi("forgot_password", { email });
  }

  return {
    ok: true,
    message:
      "If an account with that email exists, a password reset link has been sent to your email address.",
  };
}
